/**
 * Briques visuelles des pages publiques / compte (connexion, inscription,
 * vitrine, erreurs) — design BLC Médias validé (nuit + violet).
 * Aucun hook ici : utilisable depuis un composant serveur comme client.
 */
import type { ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')

/** Triangle « play » dégradé bleu → violet → rose. */
export function BrandGlyph({ size = 40, className = '' }: { size?: number; className?: string }) {
  const id = `blc-g-${size}`
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden className={className}>
      <defs>
        <linearGradient id={id} x1="6" y1="4" x2="44" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3B6BFF" />
          <stop offset="0.55" stopColor="#7C4DFF" />
          <stop offset="1" stopColor="#FF3D9A" />
        </linearGradient>
      </defs>
      <path d="M13 7.5c0-2.7 2.9-4.4 5.3-3l23 13.6c2.3 1.4 2.3 4.7 0 6.1l-23 13.6c-2.4 1.4-5.3-.3-5.3-3.1z" fill={`url(#${id})`} />
      <path d="M13 7.5c0-2.7 2.9-4.4 5.3-3l23 13.6" fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}

/** Bloc marque : glyphe + « BLC Médias » (+ sous-titre facultatif). */
export function BrandBlock({ size = 'md', sub, className = '' }: { size?: 'sm' | 'md' | 'lg'; sub?: ReactNode; className?: string }) {
  const g = size === 'lg' ? 52 : size === 'sm' ? 30 : 40
  const t = size === 'lg' ? 'text-[26px]' : size === 'sm' ? 'text-[17px]' : 'text-[21px]'
  return (
    <span className={cx('inline-flex items-center gap-2.5', className)}>
      <BrandGlyph size={g} className="drop-shadow-[0_4px_16px_rgb(var(--a2)/.55)]" />
      <span className="leading-none text-left">
        <span className={cx('block font-extrabold tracking-tight text-white', t)}>BLC <span className="text-a4">Médias</span></span>
        {sub && <span className="block text-[11px] text-white/50 mt-1 tracking-wide">{sub}</span>}
      </span>
    </span>
  )
}

/** Fond nuit + halos violet/rose (décor absolu, à placer dans un parent relatif). */
export function GlowBackdrop({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden className={cx('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] max-w-[140vw] h-[520px] rounded-full bg-a1/25 blur-[110px]" />
      <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-[#FF3D9A]/[0.13] blur-[110px]" />
      <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-a2/[0.14] blur-[120px]" />
    </div>
  )
}

/** Page centrée (connexion, inscription, téléphone). */
export function AuthShell({ children, top }: { children: ReactNode; top?: ReactNode }) {
  return (
    <main className="min-h-screen relative flex items-center justify-center px-4 py-12 bg-sbg text-white overflow-hidden">
      <GlowBackdrop />
      {top}
      <div className="relative w-full max-w-[420px]">{children}</div>
    </main>
  )
}

/** Carte vitrée des formulaires. */
export function AuthCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('rounded-2xl bg-spanel/90 border border-white/[0.07] backdrop-blur-xl shadow-[0_24px_80px_-20px_rgb(var(--a1)/.35)] p-5 sm:p-6', className)}>
      {children}
    </div>
  )
}

/** Champ libellé + icône, style kit (bg 6 %, bordure 9 %, focus violet). */
export function AuthField({ icon, label, right, children }: { icon?: ReactNode; label: string; right?: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-medium text-white/65 mb-1.5 block">{label}</span>
      <span className="flex items-center gap-2.5 h-12 rounded-xl bg-white/[0.06] border border-white/[0.09] px-4 text-white focus-within:border-a2/70 focus-within:bg-white/[0.08] focus-within:shadow-[0_0_0_3px_rgb(var(--a2)/.15)] transition">
        {icon && <span className="text-smuted shrink-0">{icon}</span>}
        {children}
        {right}
      </span>
    </label>
  )
}

/** Classe d'un <input> nu placé dans AuthField. */
export const authInputCls = 'flex-1 min-w-0 bg-transparent outline-none text-sm text-white placeholder-smuted'

/** Message d'erreur (rouge réservé aux erreurs). */
export function AuthAlert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded-xl border border-rose-400/30 bg-rose-500/[0.08] px-3.5 py-3 flex gap-2.5 text-[13px]">
      <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
      <span className="text-rose-200/90">{children}</span>
    </div>
  )
}

/** Bouton principal pilule dégradé violet (type submit par défaut). */
export const primaryPillCls = 'w-full h-12 rounded-full text-sm font-semibold text-white inline-flex items-center justify-center gap-2 bg-gradient-to-r from-a1 via-a2 to-a3 shadow-[0_0_24px_rgb(var(--a2)/.45)] hover:brightness-110 active:scale-[0.98] transition disabled:opacity-60 disabled:pointer-events-none'

/** Séparateur « ou ». */
export function OrDivider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-[11.5px] text-white/40">
      <span className="flex-1 h-px bg-white/[0.09]" /> {children} <span className="flex-1 h-px bg-white/[0.09]" />
    </div>
  )
}
